import { defineDocument } from '../../schema';
export const gameBadBunniesDocument = defineDocument({
    id: 'game:bad-bunnies',
    kind: 'game',
    slug: 'bad-bunnies',
    route: '/discover/games/bad-bunnies',
    seo: {
        title: 'Discover Bad Bunnies',
        description: 'Bad Bunnies is a co-op strategy game for 1–16 players. Vote on action cards each turn to cull colonies and stop the bunny population from hitting 1,000.',
        imageAsset: 'coverBadBunnies',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Bad Bunnies',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'A co-op game where players vote on tactical actions to stop runaway bunny reproduction before the population grows too large.',
            },
        ],
        imageAsset: 'coverBadBunnies',
        imageAlt: 'Bad Bunnies game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The bunnies have launched a fluffy coup, and every burrow is a ticking fur-bomb. Each turn, every burrow with at least two bunnies spawns more bunnies into adjoining burrows, and the map gets messier fast.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Your squad votes on how to respond: destroy tunnels, cull colonies, or distract them with food before the swarm snowballs out of control. Wipe out every last bunny to win. Let the population hit 1,000 and the rabbits claim the planet.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1-16 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Cooperative',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Selection, Voting',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~15 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'March 25, 2026',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '69ac5a4510c9adc947dc1981',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The game begins with 10 bunnies distributed across 16 burrows connected by 24 tunnels. Each burrow has 3 tunnel connections.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players always have four action cards to choose from. Each turn, players vote on which card to play and which active burrow or tunnel to target. Only non-destroyed burrows and tunnels are valid targets. The eight possible action cards are:',
                        },
                    ],
                },
                {
                    type: 'table',
                    variant: 'default',
                    columns: [
                        [
                            {
                                type: 'text',
                                text: 'Card',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Draw %',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Effect',
                            },
                        ],
                    ],
                    rows: [
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Destroy Tunnel',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '20%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Permanently prevents bunnies from moving through a tunnel for the rest of the game.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Destroy Burrow',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '25%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Removes all bunnies, the burrow itself, and its connected tunnels from the map.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Block Burrow',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '5%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Prevents bunnies from entering or leaving a burrow and its tunnels for one turn.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Poison Colony',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '10%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Removes all bunnies from a burrow and half the bunnies from each neighboring burrow.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Cull Colony',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '10%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Removes all bunnies from a burrow.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Add Fox',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '10%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Places a fox that removes 10 bunnies per turn from a burrow on an ongoing basis.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Give Lettuce',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '5%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Skips reproduction in a burrow and its connected burrows for one turn.',
                                },
                            ],
                        ],
                        [
                            [
                                {
                                    type: 'text',
                                    text: 'Give Carrot',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: '15%',
                                },
                            ],
                            [
                                {
                                    type: 'text',
                                    text: 'Permanently skips reproduction in one burrow.',
                                },
                            ],
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'After the action takes effect, bunnies reproduce: for every 2 bunnies in a burrow, 1 new bunny is produced in each neighboring burrow. For example, 8 bunnies in a burrow would produce 4 bunnies in each neighboring burrow. Odd number of bunnies in a burrow round down (e.g. 3 bunnies produce 1, 5 bunnies produce 2).',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'During the reproduction phase, any foxes on the map remove up to 10 bunnies from their assigned burrow.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The cycle repeats until the game ends in a win or a loss. When a new turn begins, the previously played action card is replaced by a new one randomly drawn from the deck.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Bad Bunnies is fully cooperative. There is no individual scoring.',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Bunnies win',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' if the total population reaches 1,000.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Players win',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' by wiping out every bunny on the map.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Players also win',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' if all possible paths for bunny reproduction are permanently blocked, which triggers a chain reaction that destroys all remaining burrows.',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'None',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Burrow names',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: medium-high',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: strategy enthusiasts, families, solo players',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/bad-bunnies-burst-out-of-the-burrow',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Bad Bunnies blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '69ac5a4510c9adc947dc1981',
                },
            ],
        },
    ],
});
//# sourceMappingURL=bad-bunnies.js.map